import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import { API_ENDPOINTS } from '../../config/env';
import { getCurrentUserRole, getCurrentUserId } from '../../utils/auth';
import { dashboardService, User } from '../../services/dashboardService';
import { cn } from '../../utils/cn';

interface Settlement {
  id: number;
  transactionId: number;
  type: 'Admin' | 'Agent' | 'SuperAgent' | 'Company' | string;
  toWallet: string;
  amount: string;
  createtAt: string;
  createtBy: string;
  txHash: string;
  status: 'Pending' | 'Completed' | 'Failed';
}

interface SettlementFilters {
  status: string;
  dateFrom: string;
  dateTo: string;
  userId: string;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

type PayoutGroup = { transactionId: number; items: Settlement[] };

const BRAND = '#2297D1';

/** Distinct left-bar + label colors for summary tiles (blue, amber, green, sky). */
const ACCENT_METRICS = ['#2297D1', '#f59e0b', '#059669', '#0ea5e9'] as const;

const pageBgClass =
  'min-h-screen bg-gradient-to-b from-[#d4e0ed] via-[#e4eaf1] to-[#eef2f7]';

const SurfaceCard: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => (
  <section
    className={cn(
      'rounded-2xl border border-[#b8c7d4] bg-white p-[30px] shadow-[0_2px_12px_rgba(15,23,42,0.08)]',
      className
    )}
  >
    {children}
  </section>
);

const SettlementMetricTile: React.FC<{
  label: string;
  value: string | number;
  hint: string;
  accentIndex?: number;
}> = ({ label, value, hint, accentIndex = 0 }) => {
  const leftColor = ACCENT_METRICS[accentIndex % ACCENT_METRICS.length];
  return (
    <div className='rounded-[20px] overflow-hidden h-full'>
      <div
      className=" border-l border-[#dce3ea] bg-white py-2 px-[22px] h-full shadow-md"
      style={{ borderLeft: `6px solid ${leftColor}` }}
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: leftColor }}>
        {label}
      </p>
      <p className="mt-1.5 text-2xl font-bold tracking-tight text-black sm:text-3xl">{value}</p>
      <p className="mt-1 text-[11px] leading-snug text-slate-500">{hint}</p>
    </div>
    </div>
  );
};

const inputClass =
  'w-full rounded-lg border border-[#66666685] bg-[#F0F0F0] p-[12px] text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#2297D1] focus:outline-none focus:ring-2 focus:ring-[#2297D1]/20';

const labelClass = 'mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500';

const typeLabel = (t: Settlement['type']) => {
  if (t === 'Admin' || t === 'Company') return 'Company (Com)';
  if (t === 'Agent') return 'Merchant (Dest)';
  if (t === 'SuperAgent') return 'Super Agent';
  return String(t);
};

const normalizeSettlementsPayload = (payload: unknown): Settlement[] => {
  if (Array.isArray(payload)) return payload as Settlement[];
  if (!payload || typeof payload !== 'object') return [];

  const root = payload as Record<string, unknown>;
  const candidates = [root.data, root.result, root.items, root.settlements];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate as Settlement[];
  }
  return [];
};

const Settlements: React.FC = () => {
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<SettlementFilters>({
    status: '',
    dateFrom: '',
    dateTo: '',
    userId: '',
  });
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 25,
  });
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showUserDropdown, setShowUserDropdown] = useState<boolean>(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const [expandedTransactionId, setExpandedTransactionId] = useState<number | null>(null);
  const [lastChecked, setLastChecked] = useState<string>('');

  const userRole = getCurrentUserRole();
  const userId = getCurrentUserId();

  const fetchUsers = useCallback(async () => {
    try {
      const usersData = await dashboardService.getUsers();
      setUsers(usersData);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, []);

  const fetchSettlements = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const params = new URLSearchParams();

      if (filters.status) params.append('status', filters.status);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.userId) params.append('userid', filters.userId);
      params.append('page', pagination.currentPage.toString());
      params.append('pageSize', pagination.itemsPerPage.toString());

      const url = `${API_ENDPOINTS.TRANSACTIONS}/payouts?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const normalized = normalizeSettlementsPayload(data);
      setSettlements(normalized);

      if (normalized.length < pagination.itemsPerPage) {
        setPagination((prev) => ({
          ...prev,
          totalPages: prev.currentPage,
          totalItems: (prev.currentPage - 1) * prev.itemsPerPage + normalized.length,
        }));
      } else {
        setPagination((prev) => ({
          ...prev,
          totalPages: prev.currentPage + 1,
          totalItems: prev.currentPage * prev.itemsPerPage,
        }));
      }
      setLastChecked(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Error fetching settlements:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch settlements');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.currentPage, pagination.itemsPerPage]);

  useEffect(() => {
    if (userRole === 'Admin' || userRole === 'SuperAgent') {
      fetchUsers();
    }
  }, [userRole, fetchUsers]);

  useEffect(() => {
    fetchSettlements();
  }, [fetchSettlements]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFilterChange = (key: keyof SettlementFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleUserSelect = (selectedUserId: string) => {
    setFilters((prev) => ({ ...prev, userId: selectedUserId }));
    setSearchTerm('');
    setShowUserDropdown(false);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handleRefresh = () => {
    fetchSettlements();
  };

  const formatDateTime = (dateString: string): string => new Date(dateString).toLocaleString();

  const truncateAddress = (address: string): string => {
    if (address.length <= 10) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const parseAmount = (s: string): number => {
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
  };

  const getFilteredUsers = (): User[] => {
    let filteredUsers: User[] = [];
    if (userRole === 'Admin') {
      filteredUsers = users;
    } else if (userRole === 'SuperAgent') {
      filteredUsers = users.filter(
        (user) =>
          user.id === parseInt(userId || '0', 10) ||
          (user.role === 'Agent' && user.id !== parseInt(userId || '0', 10))
      );
    } else {
      filteredUsers = users.filter((user) => user.id === parseInt(userId || '0', 10));
    }
    return filteredUsers.filter(
      (user) =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const payoutGroups = useMemo((): PayoutGroup[] => {
    const map = new Map<number, Settlement[]>();
    for (const s of settlements) {
      if (!map.has(s.transactionId)) map.set(s.transactionId, []);
      map.get(s.transactionId)!.push(s);
    }
    const groups = Array.from(map.entries()).map(([transactionId, items]) => ({ transactionId, items }));
    return groups.sort((a, b) => {
      const ta = Math.max(...a.items.map((i) => new Date(i.createtAt).getTime()));
      const tb = Math.max(...b.items.map((i) => new Date(i.createtAt).getTime()));
      return tb - ta;
    });
  }, [settlements]);

  const usdtInView = useMemo(
    () => settlements.reduce((sum, s) => sum + parseAmount(s.amount), 0),
    [settlements]
  );

  const needsFollowUpCount = useMemo(
    () => settlements.filter((s) => s.status === 'Pending' || s.status === 'Failed').length,
    [settlements]
  );

  const scopeLabel =
    userRole === 'Admin' ? 'Platform' : userRole === 'SuperAgent' ? 'Super Agent' : 'Merchant';

  const statusTab = (key: '' | 'Pending' | 'Completed' | 'Failed', label: string) => {
    const active = filters.status === key;
    return (
      <button
        type="button"
        onClick={() => handleFilterChange('status', key)}
        className={cn(
          'px-[13px] py-2 rounded-[6px] text-sm font-semibold transition-colors border-[1px]',
          active ? 'border-[#2297D1] bg-[#2297D1] text-white shadow-sm' : 'border-[#e0e3e7] bg-white text-slate-600 hover:bg-slate-50'
        )}
      >
        {label}
      </button>
    );
  };

  const groupRollupStatus = (items: Settlement[]): Settlement['status'] => {
    if (items.every((i) => i.status === 'Completed')) return 'Completed';
    if (items.some((i) => i.status === 'Failed')) return 'Failed';
    return 'Pending';
  };

  const groupCompletedTotal = (items: Settlement[]) =>
    items.filter((i) => i.status === 'Completed').reduce((sum, i) => sum + parseAmount(i.amount), 0);

  const statusBadgeClass = (status: string) => {
    if (status === 'Completed') return 'bg-sky-100 text-sky-800 ring-1 ring-sky-200';
    if (status === 'Pending') return 'bg-amber-100 text-amber-900 ring-1 ring-amber-200';
    return 'bg-rose-100 text-rose-900 ring-1 ring-rose-200';
  };

  const exportCsv = () => {
    const header = ['id', 'transactionId', 'type', 'amount', 'status', 'createtAt', 'txHash', 'toWallet'];
    const lines = [
      header.join(','),
      ...settlements.map((s) =>
        [
          s.id,
          s.transactionId,
          s.type,
          s.amount,
          s.status,
          `"${s.createtAt}"`,
          s.txHash,
          `"${s.toWallet}"`,
        ].join(',')
      ),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `settlements-page-${pagination.currentPage}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;
    const items: number[] = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, pagination.currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    for (let page = startPage; page <= endPage; page++) items.push(page);

    const btn =
      'rounded-lg border border-[#d2d8dd] px-3 py-1.5 text-sm font-semibold transition-colors disabled:opacity-40';
    return (
      <div className="flex flex-wrap items-center justify-center gap-2 py-4">
        <button
          type="button"
          className={cn(btn, 'bg-white text-slate-700 hover:bg-slate-50')}
          disabled={pagination.currentPage === 1}
          onClick={() => handlePageChange(pagination.currentPage - 1)}
        >
          Prev
        </button>
        {items.map((page) => (
          <button
            key={page}
            type="button"
            className={cn(
              btn,
              page === pagination.currentPage
                ? 'border-[#2297D1] bg-[#2297D1] text-white'
                : 'bg-white text-slate-700 hover:bg-slate-50'
            )}
            onClick={() => handlePageChange(page)}
          >
            {page}
          </button>
        ))}
        <button
          type="button"
          className={cn(btn, 'bg-white text-slate-700 hover:bg-slate-50')}
          disabled={pagination.currentPage === pagination.totalPages}
          onClick={() => handlePageChange(pagination.currentPage + 1)}
        >
          Next
        </button>
      </div>
    );
  };

  if (loading && settlements.length === 0) {
    return (
      <div className={cn(pageBgClass, 'px-3 py-8 sm:px-4')}>
        <div className="mx-auto flex max-w-[1440px] items-center justify-center py-24">
          <Spinner animation="border" role="status" style={{ color: BRAND }}>
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(pageBgClass, 'px-3 py-3 sm:px-4 sm:py-4 homeBg')}>
      <div className="mx-auto w-full max-w-[1440px] space-y-[40px] pt-3">
        <SurfaceCard className="p-4 shadow-lg blurLayerInteractiveInteractive">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="mb-0.5 text-base uppercase text-[#4A4A4A]">Payout tracking</p>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Settlements</h1>
              <p className="mt-1 max-w-2xl text-xs text-slate-600 sm:text-sm">
                Track USDC payouts (Com + Dest), chain confirmations, and follow-up work across merchants from one workspace.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleRefresh}
                disabled={loading}
                className="border-[1px] border-[#d2d8dd] px-[13px] py-2 rounded-[6px] text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {loading ? 'Refreshing…' : 'Refresh'}
              </button>
              <button
                type="button"
                onClick={exportCsv}
                className="bg-[#2297D1] px-[13px] py-2 rounded-[6px] text-sm font-semibold text-white shadow-sm hover:bg-[#1b7fb3]"
              >
                Export CSV
              </button>
            </div>
          </div>
          <div className="mt-3 grid gap-4  pt-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl bg-[#F0F0F0] px-4 py-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">Groups in view</p>
              <p className="mt-0.5 text-base font-bold text-slate-900">{payoutGroups.length}</p>
            </div>
            <div className="rounded-xl bg-[#F0F0F0] px-4 py-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">USDC in view</p>
              <p className="mt-0.5 text-base font-bold text-slate-900">{usdtInView.toFixed(3)}</p>
            </div>
            <div className="rounded-xl bg-[#F0F0F0] px-4 py-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">Scope</p>
              <p className="mt-0.5 text-base font-bold text-slate-900">{scopeLabel}</p>
            </div>
            <div className="rounded-xl bg-[#F0F0F0] px-4 py-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">Needs follow-up</p>
              <p className="mt-0.5 text-base font-bold text-slate-900">{needsFollowUpCount}</p>
            </div>
          </div>
        </SurfaceCard>

        <SurfaceCard className="p-4 shadow-lg">
          <h2 className="text-base font-semibold text-slate-900">View controls</h2>
          <p className="text-[11px] sm:text-sm text-slate-500">Filter payout lines by stage, time range, and account — same API parameters as before.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {statusTab('', 'All groups')}
            {statusTab('Pending', 'Waiting')}
            {statusTab('Completed', 'Completed')}
            {statusTab('Failed', 'Needs attention')}
          </div>
          <div className="mt-3  flex justify-between gap-4 items-center flex-wrap xl:flex-nowrap">
            <div className=" flex-1">
              <label className={labelClass} htmlFor="settle-from">
                From
              </label>
              <input
                id="settle-from"
                type="datetime-local"
                className={inputClass}
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </div>
            <div className="flex-1">
              <label className={labelClass} htmlFor="settle-until">
                Until
              </label>
              <input
                id="settle-until"
                type="datetime-local"
                className={inputClass}
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </div>
            {(userRole === 'Admin' || userRole === 'SuperAgent') && (
              <div className="relative lg:col-span-4" ref={userDropdownRef}>
                <label className={labelClass}>Account</label>
                <div className="relative">
                  <input
                    type="text"
                    className={cn(inputClass, 'pr-14')}
                    placeholder="Search merchant or agent"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowUserDropdown(true);
                    }}
                    onFocus={() => setShowUserDropdown(true)}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-semibold text-[#2297D1]"
                    onClick={() => setShowUserDropdown((s) => !s)}
                  >
                    {showUserDropdown ? '-' : '+'}
                  </button>
                </div>
                {showUserDropdown && (
                  <div className="absolute z-30 mt-1 max-h-52 w-full overflow-y-auto rounded-xl border border-[#d9dde2] bg-white shadow-lg">
                    <button
                      type="button"
                      className={cn(
                        'w-full border-b border-[#eef1f4] px-3 py-2.5 text-left text-sm',
                        filters.userId === '' ? 'bg-[#2297D1] text-white' : 'text-slate-700 hover:bg-slate-50'
                      )}
                      onClick={() => handleUserSelect('')}
                    >
                      All accounts
                    </button>
                    {getFilteredUsers().map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        className={cn(
                          'w-full border-b border-[#eef1f4] px-3 py-2.5 text-left text-sm',
                          filters.userId === user.id.toString() ? 'bg-[#2297D1] text-white' : 'text-slate-700 hover:bg-slate-50'
                        )}
                        onClick={() => handleUserSelect(user.id.toString())}
                      >
                        <span className="font-semibold">{user.name}</span>
                        <span
                          className={cn(
                            'block text-xs',
                            filters.userId === user.id.toString() ? 'text-white/90' : 'text-slate-500'
                          )}
                        >
                          {user.email} · {user.role}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="  lg:text-left">
              <p className="text-[10px] sm:text-base font-semibold uppercase tracking-[0.12em] text-black">Current view</p>
              <p className="mt-1 text-xs text-slate-600">
              Showing every payout group visible to Merchant. Refresh or export from the header when you <br className='hidden xl:flex'/> need the latest copy.
              </p>
            </div>
          </div>
        </SurfaceCard>

        <div className="grid grid-cols-2 gap-4 lg:gap-[30px] lg:grid-cols-4">
          <SettlementMetricTile
            accentIndex={0}
            label="Payout groups"
            value={payoutGroups.length}
            hint="Transaction groups with payouts in this view."
          />
          <SettlementMetricTile
            accentIndex={1}
            label="USDC in view"
            value={usdtInView.toFixed(3)}
            hint="Total USDC across displayed payout lines (Com + Dest)."
          />
          <SettlementMetricTile
            accentIndex={2}
            label="Payout lines"
            value={settlements.length}
            hint="Individual payout records on this page."
          />
          <SettlementMetricTile
            accentIndex={3}
            label="Needs follow-up"
            value={needsFollowUpCount}
            hint="Lines pending confirmation or failed on-chain."
          />
        </div>

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
        )}

        <SurfaceCard className="!p-0 overflow-hidden">
          <div className="flex flex-col gap-0.5 border-b-[1px] border-slate-200/90 bg-[#F6F6F6] py-[18px] px-[30px] sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Payout groups</h2>
              <p className="mt-2 text-sm text-slate-500">Open any group for the full payout breakdown, chain records, and settlement details.</p>
            </div>
            <p className="text-sm text-slate-500">
              <span className="font-semibold text-slate-800">{payoutGroups.length}</span> groups in view
            </p>
          </div>
          <div className="space-y-6 bg-[#F6F6F6] p-[30px]">
            {loading && settlements.length > 0 && (
              <div className="flex justify-center py-4">
                <Spinner animation="border" size="sm" style={{ color: BRAND }} />
              </div>
            )}
            {payoutGroups.map(({ transactionId, items }) => {
              const first = items[0];
              const rollup = groupRollupStatus(items);
              const paid = groupCompletedTotal(items);
              const uniqueWallets = new Set(items.map((i) => i.toWallet)).size;
              const withHash = items.filter((i) => i.txHash).length;
              const latest = Math.max(...items.map((i) => new Date(i.createtAt).getTime()));
              const polygonUrl = items.find((i) => i.txHash)?.txHash;
              const expanded = expandedTransactionId === transactionId;

              return (
                <div
                  key={transactionId}
                  className="rounded-2xl border border-[#d1d9e0] p-4 shadow-sm transition-colors"
                  style={{
                    borderLeft: `6px solid ${BRAND}`,
                    background: expanded ? '#2297D10D' : ' ',
                  }}
                >
                  {/* Card header row */}
                  <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Transaction #{transactionId}</p>
                      <p className="text-base font-bold uppercase text-slate-900">
                        {typeLabel(first.type)} / {first.createtBy || '—'}
                      </p>
                      <p className="mt-0.5 text-sm text-slate-500">Updated {formatDateTime(new Date(latest).toISOString())}</p>
                    </div>
                    <div className="text-left lg:text-right">
                      <p className="text-base font-bold text-slate-900">{paid.toFixed(3)} USDC</p>
                      <p className="text-xs text-slate-400">Total paid out</p>
                      <span className={cn('mt-1 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase', statusBadgeClass(rollup))}>
                        {rollup}
                      </span>
                    </div>
                  </div>

                  {/* Stats row */}
                  <div className="mt-3 grid gap-2 border-t border-slate-200/60 pt-3 sm:grid-cols-3">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Payout lines</p>
                      <p className="mt-0.5 text-base font-semibold text-slate-900">{items.length}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Payout destinations</p>
                      <p className="mt-0.5 text-base font-semibold text-slate-900">{uniqueWallets}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Chain records</p>
                      <p className="mt-0.5 text-base font-semibold text-slate-900">{withHash} TX</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                    {polygonUrl && (
                      <a
                        href={`https://polygonscan.com/tx/${polygonUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-semibold text-[#2297D1] hover:underline"
                      >
                        View on PolygonScan
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => setExpandedTransactionId(expanded ? null : transactionId)}
                      className="border-[1px] border-[#d2d8dd] bg-white px-[13px] py-2 rounded-[6px] text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      {expanded ? 'Hide payout details' : 'Show payout details'}
                    </button>
                  </div>

                  {/* Expanded payout lines */}
                  {expanded && (
                    <div className="mt-4 rounded-xl  overflow-hidden">
                      {/* Expanded header */}
                      <div className="flex items-center justify-between   px-4 py-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Detailed payout lines</p>
                          <p className="text-xs text-slate-500">Review each recipient and open the matching chain record when it is available.</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-500">Group status: {rollup}</p>
                          <p className="text-sm font-bold text-slate-900">{paid.toFixed(2)} USDC</p>
                        </div>
                      </div>
                      {/* Per-item rows */}
                      <div className=" ">
                        {items.map((s) => (
                          <div key={s.id} className="px-4 py-3">
                            <div className="flex items-center justify-between">
                              <p className="text-sm text-slate-500">{formatDateTime(s.createtAt)}</p>
                              {s.txHash && (
                                <a
                                  href={`https://polygonscan.com/tx/${s.txHash}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm font-semibold text-[#2297D1] hover:underline"
                                >
                                  Open chain tx
                                </a>
                              )}
                            </div>
                            <div className="mt-1.5 flex items-center justify-between">
                              <div>
                                <span className={cn('inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold', statusBadgeClass(s.status))}>
                                  {s.status}
                                </span>
                                <p className="mt-1 text-sm font-semibold text-slate-900">
                                  {typeLabel(s.type)}
                                  {userRole === 'Admin' && (
                                    <span className="ml-2 text-xs font-normal text-slate-500" title={s.toWallet}>
                                      {truncateAddress(s.toWallet)}
                                    </span>
                                  )}
                                </p>
                              </div>
                              <p className="text-sm font-bold text-slate-900">{parseAmount(s.amount).toFixed(2)} USDC</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {!loading && settlements.length === 0 && (
              <p className="py-10 text-center text-sm text-slate-500">No settlements found</p>
            )}
          </div>
          {renderPagination()}
        </SurfaceCard>
      </div>
    </div>
  );
};

export default Settlements;
